/**
 * React Hook for Performance Monitoring
 * Provides easy integration with the performance monitoring service
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import performanceMonitor from '../services/performanceMonitor';

interface PerformanceMetrics {
  LCP?: number;
  CLS?: number;
  FCP?: number;
  TTFB?: number;
  INP?: number;
  [key: string]: number | undefined;
}

interface UsePerformanceOptions {
  enabled?: boolean;
  reportingEndpoint?: string;
  onMetricUpdate?: (metrics: PerformanceMetrics) => void;
  onBudgetExceeded?: (metric: string, value: number, budget: number) => void;
}

export function usePerformance(options: UsePerformanceOptions = {}) {
  const {
    enabled = true,
    reportingEndpoint,
    onMetricUpdate,
    onBudgetExceeded,
  } = options;

  const [metrics, setMetrics] = useState<PerformanceMetrics>({});
  const [isMonitoring, setIsMonitoring] = useState(enabled);
  const measureRefs = useRef<Map<string, number>>(new Map());
  const metricsUpdateRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enabled) {
      performanceMonitor.disable();
      return;
    }

    performanceMonitor.enable();

    if (reportingEndpoint) {
      performanceMonitor.setReportingEndpoint(reportingEndpoint);
    }

    // Update metrics less frequently to reduce re-renders
    const updateMetrics = () => {
      const currentMetrics = performanceMonitor.getMetrics();
      const metricsMap: PerformanceMetrics = {};
      
      currentMetrics.forEach(metric => {
        if (['LCP', 'CLS', 'FCP', 'TTFB', 'INP'].includes(metric.name)) {
          metricsMap[metric.name] = metric.value;
        }
      });

      // Provide default values for metrics that haven't been measured yet
      // INP requires user interaction, CLS requires layout shifts
      if (metricsMap.INP === undefined) {
        // Set to 0 initially - will update when user interacts
        metricsMap.INP = 0;
      }
      if (metricsMap.CLS === undefined) {
        // Set to 0 initially - will update if layout shifts occur
        metricsMap.CLS = 0;
      }

      // Only update state if metrics actually changed
      setMetrics(prevMetrics => {
        const hasChanged = Object.keys(metricsMap).some(
          key => prevMetrics[key] !== metricsMap[key]
        );
        return hasChanged ? metricsMap : prevMetrics;
      });
      
      onMetricUpdate?.(metricsMap);
    };

    // Initial update
    updateMetrics();
    
    // Update every 5 seconds instead of every second
    metricsUpdateRef.current = setInterval(updateMetrics, 5000);

    // Listen for budget exceeded events
    const handleBudgetExceeded = (event: CustomEvent) => {
      const { metric, budget } = event.detail;
      onBudgetExceeded?.(metric.name, metric.value, budget);
    };

    window.addEventListener('performance-budget-exceeded', handleBudgetExceeded as EventListener);

    return () => {
      if (metricsUpdateRef.current) {
        clearInterval(metricsUpdateRef.current);
      }
      window.removeEventListener('performance-budget-exceeded', handleBudgetExceeded as EventListener);
    };
  }, [enabled, reportingEndpoint, onMetricUpdate, onBudgetExceeded]);

  // Start timing a custom metric
  const startMeasure = useCallback((name: string) => {
    measureRefs.current.set(name, performance.now());
    performanceMonitor.mark(`${name}-start`);
  }, []);

  // End timing a custom metric
  const endMeasure = useCallback((name: string) => {
    const start = measureRefs.current.get(name);
    if (start !== undefined) {
      const duration = performance.now() - start;
      performanceMonitor.mark(`${name}-end`);
      performanceMonitor.measure(name, `${name}-start`, `${name}-end`);
      measureRefs.current.delete(name);
      return duration;
    }
    return null;
  }, []);

  // Measure a function execution time
  const measureFunction = useCallback(<T extends any[], R>(
    name: string,
    fn: (...args: T) => R
  ): ((...args: T) => R) => {
    return (...args: T): R => {
      startMeasure(name);
      try {
        const result = fn(...args);
        if (result instanceof Promise) {
          return result.finally(() => endMeasure(name)) as R;
        }
        endMeasure(name);
        return result;
      } catch (error) {
        endMeasure(name);
        throw error;
      }
    };
  }, [startMeasure, endMeasure]);

  // Get current performance report
  const getReport = useCallback(() => {
    return performanceMonitor.getReport();
  }, []);

  // Force send performance data
  const flush = useCallback(() => {
    return performanceMonitor.flush();
  }, []);

  // Toggle monitoring
  const toggleMonitoring = useCallback(() => {
    if (isMonitoring) {
      performanceMonitor.disable();
    } else {
      performanceMonitor.enable();
    }
    setIsMonitoring(!isMonitoring);
  }, [isMonitoring]);

  return {
    metrics,
    isMonitoring,
    startMeasure,
    endMeasure,
    measureFunction,
    getReport,
    flush,
    toggleMonitoring,
  };
}

// Hook for component render performance - disabled in production
export function useRenderPerformance(componentName: string) {
  const renderCount = useRef(0);
  const renderTimes = useRef<number[]>([]);
  const lastRenderTime = useRef<number>(0);
  const isProduction = (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') || import.meta.env?.MODE === 'production';
  
  useEffect(() => {
    // Skip performance tracking in production
    if (isProduction) return;
    
    const renderTime = performance.now();
    const timeSinceLastRender = lastRenderTime.current ? renderTime - lastRenderTime.current : 0;
    
    renderCount.current++;
    renderTimes.current.push(timeSinceLastRender);
    
    // Keep only last 10 render times
    if (renderTimes.current.length > 10) {
      renderTimes.current.shift();
    }

    lastRenderTime.current = renderTime;

    // Only log extremely slow renders (over 100ms) and limit frequency
    if (timeSinceLastRender > 100 && renderCount.current % 10 === 0) {
      console.warn(`Slow render in ${componentName}: ${timeSinceLastRender.toFixed(2)}ms (sample ${renderCount.current})`);
    }
  });

  return {
    renderCount: renderCount.current,
    averageRenderTime: renderTimes.current.reduce((a, b) => a + b, 0) / renderTimes.current.length || 0,
    lastRenderTime: renderTimes.current[renderTimes.current.length - 1] || 0,
  };
}

// Hook for tracking user interactions
export function useInteractionTracking(interactionName: string) {
  const startTime = useRef<number | null>(null);

  const startInteraction = useCallback(() => {
    startTime.current = performance.now();
    performanceMonitor.mark(`${interactionName}-start`);
  }, [interactionName]);

  const endInteraction = useCallback((success = true) => {
    if (startTime.current !== null) {
      const duration = performance.now() - startTime.current;
      performanceMonitor.mark(`${interactionName}-end`);
      performanceMonitor.measure(
        `${interactionName}-${success ? 'success' : 'failure'}`,
        `${interactionName}-start`,
        `${interactionName}-end`
      );
      startTime.current = null;
      return duration;
    }
    return null;
  }, [interactionName]);

  return { startInteraction, endInteraction };
}